import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AddIcon from 'assets/images/todo/icon-add.svg?react'
import NoSearchResultIcon from 'assets/images/todo/icon_no_search.svg?react'
import NoTasksIcon from 'assets/images/todo/icon_no_tasks.svg?react'
import RecurrenceEditor from 'pages/calendar/components/EventEditor/RecurrenceEditor'
import settings from 'popups/Settings'
import { useTranslation } from 'react-i18next'
import { FilterType, SortType } from '../../modules/TodoFilter'
import { getSortedFolders } from '../../modules/TodoFolderSync'
import { TodoItem } from '../../modules/TodoItem'
import { updateTaskPriority, updateTaskStatus } from '../../modules/TodoManager'
import { handleTodoDelete } from '../../utils/todoDeleteUtils'
import CalendarDatePicker from '../DatePicker'
import { getDefaultFolder } from '../TodoEditor'
import { getFolderColor, getFolderIsReadOnly, getFolderName } from './folderUtils'
import { useCollapsedSections } from './hooks/useCollapsedSections'
import { useDatePicker } from './hooks/useDatePicker'
import { useQuickAdd } from './hooks/useQuickAdd'
import { useRecurrenceEditor } from './hooks/useRecurrenceEditor'
import { useTodoFiltering } from './hooks/useTodoFiltering'
import { useTodoList } from './hooks/useTodoList'
import { useTodoRenderData } from './hooks/useTodoRenderData'
import QuickAddTaskEditor from './QuickAddTaskEditor'
import styles from './style.module.scss'
import TodoCompleteListHeader from './TodoCompleteListHeader'
import TodoSectionListView from './TodoSectionListView/TodoSectionListView'
import TodoSortInfoListHeader from './TodoSortInfoListHeader'
import { calculateSections, supportsCollapsibleSections } from './utils/todoSectionUtils'

interface TodoListProps {
  selectedTodo: TodoItem | null
  initSearchText?: string
  currentFilter: FilterType
  sortOption?: SortType
  sortAscending?: boolean
  focusDate?: Date | null
  locale: string
  onTodoSelect: (todo: TodoItem | null) => void
  onSortDirectionToggle: () => void
  onSortChange: (sortOption: string) => void
  onRemoveCompletedTodo?: (key: string) => void
}

/**
 * Add Task 트리거 표시 여부 확인
 */
function shouldShowAddTaskTrigger(currentFilter: FilterType): boolean {
  return (
    currentFilter !== FilterType.Completed &&
    currentFilter !== FilterType.Overdue &&
    currentFilter !== FilterType.FocusDate
  )
}

const TodoList: React.FC<TodoListProps> = ({
  selectedTodo,
  initSearchText,
  currentFilter,
  sortOption = SortType.Created,
  sortAscending = true,
  focusDate = null,
  locale,
  onTodoSelect,
  onSortDirectionToggle,
  onSortChange,
  onRemoveCompletedTodo,
}) => {
  const { t } = useTranslation()
  const todoSettings = settings.getValue('todo') || {}
  const defaultSettingFolder = todoSettings.defaultFolder || null
  const lastAddedFolder = todoSettings.lastAddedFolder || null
  const [searchText, setSearchText] = useState(initSearchText || '')
  const layoutRef = useRef<HTMLDivElement>(null)

  // 할일 목록 로드
  const { todoList, folders, loadTodos } = useTodoList(currentFilter, focusDate)

  // Quick Add 기능
  const {
    showQuickAddTask,
    quickAddTaskRef,
    onClickQuickAddTask,
    handleQuickAddTask,
    handleQuickAddCancel,
    hideQuickAddTask,
  } = useQuickAdd(currentFilter, onTodoSelect, loadTodos, folders)

  // Date picker 상태 관리
  const { datePickerState, openDatePicker, closeDatePicker, handleDatePickerConfirm } =
    useDatePicker()

  // Recurrence editor 상태 관리
  const {
    recurrenceEditorState,
    openRecurrenceEditor,
    closeRecurrenceEditor,
    handleRecurrenceSave,
  } = useRecurrenceEditor()

  // Complete toggle 핸들러
  const handleCompleteToggle = useCallback((todo: TodoItem, isCompleted: boolean) => {
    updateTaskStatus(todo, isCompleted)
  }, [])

  // Important toggle 핸들러
  const handleImportantToggle = useCallback((todo: TodoItem, isImportant: boolean) => {
    updateTaskPriority(todo, isImportant)
  }, [])

  // Todo 삭제
  const handleTodoDeleteCallback = useCallback(
    async (todo: TodoItem) => {
      await handleTodoDelete({
        todo,
        t,
        onSuccess: () => {
          loadTodos()
        },
      })
    },
    [t, loadTodos],
  )

  // Todo 업데이트
  const handleTodoUpdate = useCallback(() => {
    loadTodos()
  }, [loadTodos])

  // Completed todo 삭제
  const handleRemoveCompletedTodo = useCallback(
    (key: string) => {
      if (onRemoveCompletedTodo) {
        onRemoveCompletedTodo(key)
      }
    },
    [onRemoveCompletedTodo],
  )

  // 검색어 동기화
  useEffect(() => {
    setSearchText(initSearchText || '')
  }, [initSearchText])

  // selectedTodo가 있으면 QuickAddTask 숨김
  useEffect(() => {
    if (selectedTodo) {
      hideQuickAddTask()
    }
  }, [selectedTodo, hideQuickAddTask])

  // filter 변경 시 quickAddEditor 초기화
  useEffect(() => {
    if (showQuickAddTask && quickAddTaskRef.current) {
      quickAddTaskRef.current.clearAll()
    }
  }, [currentFilter, showQuickAddTask])

  // 필터링과 정렬된 데이터
  const { sortedAndFilteredTodos } = useTodoFiltering(
    todoList,
    searchText,
    sortOption,
    sortAscending,
    selectedTodo,
    onTodoSelect,
    locale,
  )

  // 섹션 접기/펼치기 상태
  const { collapsedSectionKeys, setCollapsedSectionKeys } = useCollapsedSections(currentFilter)

  // 기본 폴더 계산
  const defaultFolder = useMemo(() => {
    const sortedFolders = getSortedFolders()
    return getDefaultFolder(defaultSettingFolder, lastAddedFolder, sortedFolders)
  }, [defaultSettingFolder, lastAddedFolder])

  // Memoized folder utility functions
  const memoizedGetFolderName = useCallback(
    (folderUrl: string) => getFolderName(folders, folderUrl),
    [folders],
  )

  const memoizedGetFolderColor = useCallback(
    (folderUrl: string) => getFolderColor(folders, folderUrl),
    [folders],
  )

  const memoizedGetFolderIsReadOnly = useCallback(
    (folderUrl: string) => getFolderIsReadOnly(folders, folderUrl),
    [folders],
  )

  // 섹션 계산
  const currentSections = useMemo(
    () => calculateSections(currentFilter, sortedAndFilteredTodos, locale, t),
    [currentFilter, sortedAndFilteredTodos, locale, t],
  )

  // 현재 필터에 따라 접힌 키들 가져오기
  const currentCollapsedKeys = useMemo(() => {
    return supportsCollapsibleSections(currentFilter) ? collapsedSectionKeys : []
  }, [currentFilter, collapsedSectionKeys])

  // 렌더링 데이터 준비
  const { todoRenderDataList } = useTodoRenderData(
    currentSections,
    currentCollapsedKeys,
    memoizedGetFolderName,
    memoizedGetFolderColor,
    memoizedGetFolderIsReadOnly,
    handleTodoUpdate,
    handleTodoDeleteCallback,
    {
      onOpenDatePicker: openDatePicker,
      onOpenRecurrenceEditor: openRecurrenceEditor,
    },
  )

  // UI 표시 조건
  const shouldShowAddTask = shouldShowAddTaskTrigger(currentFilter)
  const shouldShowSortHeader =
    currentFilter === FilterType.All ||
    currentFilter === FilterType.Today ||
    currentFilter === FilterType.Important ||
    currentFilter === FilterType.FocusDate
  const shouldShowCompletedSortHeader = currentFilter === FilterType.Completed

  // Completed todos 개수 계산
  const completedTodosCount = useMemo(() => {
    return currentFilter === FilterType.Completed ? sortedAndFilteredTodos.length : 0
  }, [currentFilter, sortedAndFilteredTodos.length])

  // Quick Add Task 트리거 렌더링
  const renderQuickAddTaskTrigger = useCallback(
    () => (
      <div onClick={onClickQuickAddTask}>
        <div className={styles.addTodoTriggerWrapper}>
          <div className={styles.addTodoIconBox}>
            <AddIcon className={styles.addTodoIcon} />
          </div>
          <div className={styles.addTodoText}>{t('todo.todolist.quickAddTask')}</div>
        </div>
      </div>
    ),
    [onClickQuickAddTask, t],
  )

  // Empty view 렌더링
  const renderEmptyView = useCallback(() => {
    const isOverdueFilter = currentFilter === FilterType.Overdue

    return (
      <div className={styles.emptyViewContainer}>
        <div className={styles.emptyViewContent}>
          <NoTasksIcon className={styles.emptyViewIcon} />
          <div className={styles.emptyViewTitle}>
            {isOverdueFilter
              ? t('todo.todolist.emptyOverdue.title')
              : t('todo.todolist.emptyDefault.title')}
          </div>
          <div className={styles.emptyViewDescription}>
            {isOverdueFilter
              ? t('todo.todolist.emptyOverdue.description')
              : t('todo.todolist.emptyDefault.description')}
          </div>
        </div>
      </div>
    )
  }, [currentFilter, t])

  // No search result view 렌더링
  const renderNoSearchResult = useCallback(
    () => (
      <div className={styles.emptyViewContainer}>
        <div className={styles.emptyViewContent}>
          <NoSearchResultIcon className={styles.emptyViewIcon} />
          <div className={styles.emptyViewTitle}>{t('todo.todolist.noSearchResult')}</div>
        </div>
      </div>
    ),
    [t],
  )

  return (
    <div className={styles.todoListContainer} ref={layoutRef}>
      {/* Sort Header */}
      {shouldShowSortHeader && (
        <TodoSortInfoListHeader
          currentFilter={currentFilter}
          sortOption={sortOption}
          sortAscending={sortAscending}
          onSortDirectionToggle={onSortDirectionToggle}
          onSortChange={onSortChange}
        />
      )}

      {/* Completed Sort Header */}
      {shouldShowCompletedSortHeader && (
        <TodoCompleteListHeader
          currentFilter={currentFilter}
          sortOption={sortOption}
          sortAscending={sortAscending}
          onSortDirectionToggle={onSortDirectionToggle}
          onSortChange={onSortChange}
          onRemoveCompletedTodo={handleRemoveCompletedTodo}
          completedCount={completedTodosCount}
        />
      )}

      {/* Add Task Trigger */}
      {shouldShowAddTask && renderQuickAddTaskTrigger()}

      {/* Quick Add Task */}
      {shouldShowAddTask && showQuickAddTask && (
        <QuickAddTaskEditor
          ref={quickAddTaskRef}
          defaultFolder={defaultFolder}
          currentFilter={currentFilter}
          onAdd={handleQuickAddTask}
          onCancel={handleQuickAddCancel}
          placeholder={t('todo.todolist.quickAddTask')}
        />
      )}

      {/* Empty View */}
      {sortedAndFilteredTodos.length === 0 && searchText === '' && renderEmptyView()}
      {sortedAndFilteredTodos.length === 0 && searchText !== '' && renderNoSearchResult()}

      {/* Todo List */}
      {sortedAndFilteredTodos.length > 0 && (
        <TodoSectionListView
          todoRenderDataList={todoRenderDataList}
          currentFilter={currentFilter}
          collapsedKeys={currentCollapsedKeys}
          onSectionCollapse={setCollapsedSectionKeys}
          selectedTodo={selectedTodo}
          onTodoSelect={onTodoSelect}
          onImportantToggle={handleImportantToggle}
          onCompleteToggle={handleCompleteToggle}
          onTodoUpdate={handleTodoUpdate}
          searchText={searchText}
        />
      )}

      {/* Global Date Picker */}
      {datePickerState.isOpen && (
        <div className={styles.datePickerOverlay} onClick={closeDatePicker}>
          <div onClick={(e) => e.stopPropagation()}>
            <CalendarDatePicker
              pickerType={datePickerState.pickerType}
              value={datePickerState.value}
              onChange={handleDatePickerConfirm}
              onClose={closeDatePicker}
            />
          </div>
        </div>
      )}

      {/* Global Recurrence Editor */}
      {recurrenceEditorState.isOpen && (
        <RecurrenceEditor
          open={recurrenceEditorState.isOpen}
          onClose={closeRecurrenceEditor}
          onSave={handleRecurrenceSave}
          startDate={recurrenceEditorState.startDate || new Date()}
          options={recurrenceEditorState.options || null}
        />
      )}
    </div>
  )
}

export default TodoList
